import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useEffect, useState } from 'react'
import { optionalEmailSchema } from '@/lib/validations'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'
import { clinicGroupService } from '@/services/clinic_group_service'
import { useAuth } from '@/hooks/useAuth'
import { useViewMode } from '@/stores/viewMode'
import { useClinicGroupClinics } from '@/hooks/useClinicGroups'
import { toast } from 'sonner'
import { ClinicGroup } from '@magnetar/admin-api-interfaces'
import { Plus, Building2, Trash2 } from 'lucide-react'
import { AssignClinicDialog } from './AssignClinicDialog'

const formSchema = z.object({
  group_name: z.string().min(1, { message: '請輸入集團名稱' }),
  group_code: z.string().optional(),
  manager_name: z.string().min(1, { message: '請輸入負責人姓名' }),
  phone: z.string().min(1, { message: '請輸入聯絡電話' }),
  email: optionalEmailSchema(),
  address: z.string().optional(),
  description: z.string().optional(),
  is_active: z.boolean(),
})

interface ClinicGroupDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
  clinicGroup?: ClinicGroup
}

export function ClinicGroupDialog({
  open,
  onOpenChange,
  onSuccess,
  clinicGroup,
}: ClinicGroupDialogProps) {
  const { token } = useAuth()
  const { id: providerId } = useViewMode()
  const isEditMode = !!clinicGroup

  // Assign clinic dialog state
  const [assignDialogOpen, setAssignDialogOpen] = useState(false)

  // Remove clinic confirmation state
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false)
  const [removingClinic, setRemovingClinic] = useState<{
    clinic_id: number
    clinic_name: string
  } | null>(null)

  // Get assigned clinics (only in edit mode)
  const {
    clinics: assignedClinics,
    isLoading: clinicsLoading,
    mutate: mutateClinics,
  } = useClinicGroupClinics(isEditMode && open ? clinicGroup?.clinic_group_id ?? null : null)

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      group_name: '',
      group_code: '',
      manager_name: '',
      phone: '',
      email: '',
      address: '',
      description: '',
      is_active: true,
    },
  })

  useEffect(() => {
    if (isEditMode && clinicGroup) {
      form.reset({
        group_name: clinicGroup.group_name,
        group_code: clinicGroup.group_code || '',
        manager_name: clinicGroup.manager_name,
        phone: clinicGroup.phone,
        email: clinicGroup.email || '',
        address: clinicGroup.address || '',
        description: clinicGroup.description || '',
        is_active: clinicGroup.is_active,
      })
    } else {
      form.reset({
        group_name: '',
        group_code: '',
        manager_name: '',
        phone: '',
        email: '',
        address: '',
        description: '',
        is_active: true,
      })
    }
  }, [isEditMode, clinicGroup, form, open])

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    if (!token) return

    const data = {
      group_name: values.group_name,
      group_code: values.group_code || null,
      manager_name: values.manager_name,
      phone: values.phone,
      email: values.email || null,
      address: values.address || null,
      description: values.description || null,
      is_active: values.is_active,
      provider_id: providerId,
    }

    if (isEditMode && clinicGroup) {
      const result = await clinicGroupService.updateClinicGroup(
        clinicGroup.clinic_group_id,
        data,
        token,
      )

      if (result.success) {
        toast.success('更新診所集團成功')
        form.reset()
        onOpenChange(false)
        onSuccess()
      } else {
        toast.error((result as { error?: { message?: string } }).error?.message || '更新診所集團失敗')
      }
    } else {
      const result = await clinicGroupService.createClinicGroup(
        {
          ...data,
          provider_id: providerId,
        },
        token,
      )

      if (result.success) {
        toast.success('新增診所集團成功')
        form.reset()
        onOpenChange(false)
        onSuccess()
      } else {
        toast.error((result as { error?: { message?: string } }).error?.message || '新增診所集團失敗')
      }
    }
  }

  const handleRemoveClinic = (clinic: { clinic_id: number; clinic_name: string }) => {
    setRemovingClinic(clinic)
    setRemoveDialogOpen(true)
  }

  const confirmRemoveClinic = async () => {
    if (!token || !removingClinic || !clinicGroup) return

    const result = await clinicGroupService.removeClinic(
      clinicGroup.clinic_group_id,
      removingClinic.clinic_id,
      token,
    )

    if (result.success) {
      toast.success(`已將「${removingClinic.clinic_name}」從集團移除`)
      mutateClinics()
    } else {
      toast.error('移除診所失敗')
    }

    setRemoveDialogOpen(false)
    setRemovingClinic(null)
  }

  const handleAssignSuccess = () => {
    mutateClinics()
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isEditMode ? '編輯診所集團' : '新增診所集團'}</DialogTitle>
            <DialogDescription>
              {isEditMode ? '更新診所集團資訊' : '建立新的診所集團'}
            </DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="group_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>集團名稱 *</FormLabel>
                    <FormControl>
                      <Input placeholder="請輸入集團名稱" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="group_code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>集團代碼</FormLabel>
                    <FormControl>
                      <Input placeholder="請輸入集團代碼" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="manager_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>負責人 *</FormLabel>
                    <FormControl>
                      <Input placeholder="請輸入負責人姓名" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>電話 *</FormLabel>
                    <FormControl>
                      <Input placeholder="請輸入聯絡電話" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>電子郵件</FormLabel>
                    <FormControl>
                      <Input placeholder="請輸入電子郵件" type="email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>地址</FormLabel>
                    <FormControl>
                      <Input placeholder="請輸入地址" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>描述</FormLabel>
                    <FormControl>
                      <Textarea placeholder="請輸入集團描述" className="min-h-[80px]" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="is_active"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                    <div className="space-y-0.5">
                      <FormLabel>啟用狀態</FormLabel>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />

              {/* Assigned Clinics Section - Only show in edit mode */}
              {isEditMode && (
                <>
                  <Separator className="my-4" />
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-medium">已指派診所</h4>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setAssignDialogOpen(true)}
                      >
                        <Plus className="mr-1 h-4 w-4" />
                        指派診所
                      </Button>
                    </div>

                    <div className="rounded-md border">
                      {clinicsLoading ? (
                        <div className="p-4 space-y-2">
                          <Skeleton className="h-4 w-full" />
                          <Skeleton className="h-4 w-full" />
                        </div>
                      ) : assignedClinics && assignedClinics.length > 0 ? (
                        <div className="divide-y max-h-[200px] overflow-y-auto">
                          {assignedClinics.map((clinic) => (
                            <div
                              key={clinic.clinic_id}
                              className="flex items-center justify-between p-3 hover:bg-muted/50"
                            >
                              <div className="flex items-center gap-2">
                                <Building2 className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm">{clinic.clinic_name}</span>
                                {!clinic.is_active && (
                                  <span className="text-xs text-muted-foreground">(已停用)</span>
                                )}
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                                onClick={() => handleRemoveClinic(clinic)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="p-4 text-center text-sm text-muted-foreground">
                          尚未指派任何診所
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      共 {assignedClinics?.length ?? 0} 間診所
                    </p>
                  </div>
                </>
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  取消
                </Button>
                <Button type="submit">{isEditMode ? '更新' : '新增'}</Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Assign Clinic Dialog */}
      {isEditMode && clinicGroup && (
        <AssignClinicDialog
          open={assignDialogOpen}
          onOpenChange={setAssignDialogOpen}
          groupId={clinicGroup.clinic_group_id}
          onSuccess={handleAssignSuccess}
        />
      )}

      {/* Remove Clinic Confirmation Dialog */}
      <AlertDialog open={removeDialogOpen} onOpenChange={setRemoveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>確定要移除嗎？</AlertDialogTitle>
            <AlertDialogDescription>
              此操作將把「{removingClinic?.clinic_name}」從集團移除。診所本身不會被刪除，之後可以重新指派。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRemoveClinic}>確定移除</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
